/* eslint-disable no-console */
import { db } from "@/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp
} from "firebase/firestore";

const state = {
  // Quiz data
  quizzes: [],
  currentQuiz: null,

  // Loading states
  loadingQuizzes: false,
  loadingCurrentQuiz: false,
  savingQuiz: false,

  // Error state
  error: null,

  // Prevent concurrent loading
  loadingPromise: null
};

const actions = {
  /**
   * Load all quizzes for the program
   */
  async loadQuizzes({ commit, getters, state }) {
    // If already loading, return the existing promise
    if (state.loadingPromise) {
      return state.loadingPromise;
    }

    const loadingPromise = (async () => {
      commit("setLoadingQuizzes", true);
      commit("setQuizzesError", null);

      try {
        const programRef = doc(db(), "programs", getters.programId);
        const quizzesRef = collection(programRef, "quizzes");
        const quizzesQuery = query(
          quizzesRef,
          where("status", "==", "Active"),
          orderBy("created", "desc")
        );
        const querySnapshot = await getDocs(quizzesQuery);

        const quizzes = [];
        querySnapshot.forEach(docSnapshot => {
          const data = docSnapshot.data();
          quizzes.push({
            ...data,
            id: docSnapshot.id,
            created: data.created?.toDate() || new Date(),
            updated: data.updated?.toDate() || new Date()
          });
        });

        commit("setQuizzes", quizzes);
        return quizzes;
      } catch (error) {
        console.error("Error loading quizzes:", error);
        commit("setQuizzesError", error.message);
        commit("setQuizzes", []);
        throw error;
      } finally {
        commit("setLoadingQuizzes", false);
        commit("setLoadingTable", false); // For backward compatibility
        commit("setLoadingPromise", null);
      }
    })();

    commit("setLoadingPromise", loadingPromise);
    return loadingPromise;
  },

  /**
   * Get quiz by ID
   */
  async getQuizById({ commit, getters }, quizId) {
    if (!quizId) {
      console.error("Quiz ID is required");
      return null;
    }

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizRef = doc(collection(programRef, "quizzes"), quizId);
      const quizDoc = await getDoc(quizRef);

      if (quizDoc.exists()) {
        const data = quizDoc.data();
        return {
          id: quizDoc.id,
          ...data,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        };
      } else {
        console.warn(`Quiz with ID ${quizId} not found`);
        return null;
      }
    } catch (error) {
      console.error("Error getting quiz by ID:", error);
      commit("setQuizzesError", error.message);
      return null;
    }
  },

  /**
   * Load current quiz details
   */
  async loadCurrentQuiz({ commit, dispatch }, quizId) {
    if (!quizId) {
      throw new Error("Quiz ID is required");
    }

    commit("setLoadingCurrentQuiz", true);
    commit("setCurrentQuiz", null);
    commit("setQuizzesError", null);

    try {
      const quiz = await dispatch("getQuizById", quizId);
      if (quiz) {
        commit("setCurrentQuiz", quiz);
        return quiz;
      } else {
        throw new Error("Quiz not found");
      }
    } catch (error) {
      console.error("Error loading current quiz:", error);
      commit("setQuizzesError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentQuiz", false);
    }
  },

  /**
   * Create a new quiz
   */
  async createQuiz({ dispatch, commit, getters }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Quiz title is required");
    }

    commit("setSavingQuiz", true);
    commit("setQuizzesError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizzesRef = collection(programRef, "quizzes");

      const titleUppercase = payload.title.toUpperCase();

      // Check for duplicate titles
      const titleQuery = query(
        quizzesRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const titleDupSnapshot = await getDocs(titleQuery);

      if (!titleDupSnapshot.empty) {
        throw new Error("A quiz with this title already exists");
      }

      // Calculate total points
      let totalPoints = 0;
      if (payload.questions && Array.isArray(payload.questions)) {
        payload.questions.forEach(question => {
          if (question.answers && Array.isArray(question.answers)) {
            question.answers.forEach(answer => {
              totalPoints += answer.points || 0;
            });
          }
        });
      }

      const quiz = {
        ...payload,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.user?.id || "system",
        updatedBy: getters.user?.id || "system",
        pointsOffered: totalPoints + (payload.passPoints || 0),
        status: payload.status || "Draft",
        questions: payload.questions || [],
        passPoints: payload.passPoints || 0,
        passingScore: payload.passingScore || 0
      };

      const newQuizRef = await addDoc(quizzesRef, quiz);

      const tmpQuiz = {
        ...quiz,
        id: newQuizRef.id,
        created: new Date(),
        updated: new Date()
      };

      commit("createQuiz", tmpQuiz);
      dispatch("setSnackbar", "Quiz created successfully");

      return {
        success: true,
        quizId: newQuizRef.id,
        quiz: tmpQuiz
      };
    } catch (error) {
      console.error("Error creating quiz:", error);
      commit("setQuizzesError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when creating quiz");
    } finally {
      commit("setSavingQuiz", false);
    }
  },

  /**
   * Update an existing quiz
   */
  async updateQuiz({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.id) {
      throw new Error("Quiz ID is required");
    }

    commit("setSavingQuiz", true);
    commit("setQuizzesError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizzesRef = collection(programRef, "quizzes");
      const quizRef = doc(quizzesRef, payload.id);

      // Verify quiz exists
      const quizDoc = await getDoc(quizRef);
      if (!quizDoc.exists()) {
        throw new Error("Quiz not found");
      }

      // Check for duplicate titles (excluding current quiz)
      if (payload.title) {
        const titleUppercase = payload.title.toUpperCase();
        const duplicateQuery = query(
          quizzesRef,
          where("titleUppercase", "==", titleUppercase)
        );
        const duplicateSnapshot = await getDocs(duplicateQuery);

        let duplicated = false;
        duplicateSnapshot.forEach(docSnapshot => {
          if (docSnapshot.id !== payload.id) {
            duplicated = true;
          }
        });

        if (duplicated) {
          throw new Error("A quiz with this title already exists");
        }
      }

      // Calculate total points
      let totalPoints = 0;
      if (payload.questions && Array.isArray(payload.questions)) {
        payload.questions.forEach(question => {
          if (question.answers && Array.isArray(question.answers)) {
            question.answers.forEach(answer => {
              totalPoints += answer.points || 0;
            });
          }
        });
      }

      const updateData = {
        ...payload,
        updated: serverTimestamp(),
        updatedBy: getters.user?.id || "system",
        pointsOffered: totalPoints + (payload.passPoints || 0)
      };

      if (payload.title) {
        updateData.titleUppercase = payload.title.toUpperCase();
      }

      await updateDoc(quizRef, updateData);

      const updatedQuiz = {
        ...quizDoc.data(),
        ...payload,
        updated: new Date(),
        updatedBy: getters.user?.id || "system",
        pointsOffered: totalPoints + (payload.passPoints || 0)
      };

      commit("updateQuiz", updatedQuiz);
      dispatch("setSnackbar", "Quiz updated successfully");

      return updatedQuiz;
    } catch (error) {
      console.error("Error updating quiz:", error);
      commit("setQuizzesError", error.message);
      if (error.message.includes("title already exists")) {
        throw error;
      }
      throw new Error("Error when updating quiz");
    } finally {
      commit("setSavingQuiz", false);
    }
  },

  /**
   * Delete a quiz
   */
  async deleteQuiz({ commit, getters, dispatch }, quizId) {
    if (!quizId) {
      throw new Error("Quiz ID is required");
    }

    commit("setQuizzesError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizRef = doc(collection(programRef, "quizzes"), quizId);

      await deleteDoc(quizRef);

      commit("deleteQuiz", quizId);
      dispatch("setSnackbar", "Quiz deleted successfully");

      return true;
    } catch (error) {
      console.error("Error deleting quiz:", error);
      commit("setQuizzesError", error.message);
      throw new Error("Error when deleting quiz");
    }
  },

  /**
   * Duplicate a quiz
   */
  async duplicateQuiz({ dispatch }, { quizId, newTitle }) {
    if (!quizId || !newTitle) {
      throw new Error("Quiz ID and new title are required");
    }

    try {
      const originalQuiz = await dispatch("getQuizById", quizId);
      if (!originalQuiz) {
        throw new Error("Original quiz not found");
      }

      const duplicatedQuizData = {
        ...originalQuiz,
        title: newTitle,
        titleUppercase: newTitle.toUpperCase(),
        status: "Draft"
      };

      // Remove ID and timestamp fields
      delete duplicatedQuizData.id;
      delete duplicatedQuizData.created;
      delete duplicatedQuizData.updated;

      const result = await dispatch("createQuiz", duplicatedQuizData);
      return result;
    } catch (error) {
      console.error("Error duplicating quiz:", error);
      throw error;
    }
  },

  /**
   * Load quizzes by category
   */
  async loadQuizzesByCategory({ commit, getters }, categoryId) {
    if (!categoryId) {
      throw new Error("Category ID is required");
    }

    commit("setLoadingQuizzes", true);
    commit("setQuizzesError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizzesRef = collection(programRef, "quizzes");
      const categoryQuery = query(
        quizzesRef,
        where("categoryId", "==", categoryId),
        orderBy("created", "desc")
      );
      const querySnapshot = await getDocs(categoryQuery);

      const quizzes = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        quizzes.push({
          ...data,
          id: docSnapshot.id,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      return quizzes;
    } catch (error) {
      console.error("Error loading quizzes by category:", error);
      commit("setQuizzesError", error.message);
      throw error;
    } finally {
      commit("setLoadingQuizzes", false);
    }
  },

  /**
   * Load recent quizzes
   */
  async loadRecentQuizzes({ commit, getters }, limitCount = 10) {
    commit("setLoadingQuizzes", true);
    commit("setQuizzesError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const quizzesRef = collection(programRef, "quizzes");
      const recentQuery = query(
        quizzesRef,
        orderBy("created", "desc"),
        limit(limitCount)
      );
      const querySnapshot = await getDocs(recentQuery);

      const quizzes = [];
      querySnapshot.forEach(docSnapshot => {
        const data = docSnapshot.data();
        quizzes.push({
          ...data,
          id: docSnapshot.id,
          created: data.created?.toDate() || new Date(),
          updated: data.updated?.toDate() || new Date()
        });
      });

      return quizzes;
    } catch (error) {
      console.error("Error loading recent quizzes:", error);
      commit("setQuizzesError", error.message);
      throw error;
    } finally {
      commit("setLoadingQuizzes", false);
    }
  },

  // State management actions
  clearQuizzesError({ commit }) {
    commit("setQuizzesError", null);
  },

  clearCurrentQuiz({ commit }) {
    commit("setCurrentQuiz", null);
  }
};

const mutations = {
  setQuizzes(state, payload) {
    state.quizzes = payload;
  },

  setCurrentQuiz(state, payload) {
    state.currentQuiz = payload;
  },

  setLoadingQuizzes(state, payload) {
    state.loadingQuizzes = Boolean(payload);
  },

  setLoadingCurrentQuiz(state, payload) {
    state.loadingCurrentQuiz = Boolean(payload);
  },

  setSavingQuiz(state, payload) {
    state.savingQuiz = Boolean(payload);
  },

  setQuizzesError(state, payload) {
    state.error = payload;
  },

  createQuiz(state, payload) {
    state.quizzes = [payload, ...state.quizzes];
  },

  updateQuiz(state, payload) {
    state.quizzes = state.quizzes.map(item => {
      if (item.id === payload.id) {
        return { ...item, ...payload };
      }
      return item;
    });

    // Update current quiz if it matches
    if (state.currentQuiz && state.currentQuiz.id === payload.id) {
      state.currentQuiz = { ...state.currentQuiz, ...payload };
    }
  },

  deleteQuiz(state, quizId) {
    state.quizzes = state.quizzes.filter(item => item.id !== quizId);

    // Clear current quiz if it matches
    if (state.currentQuiz && state.currentQuiz.id === quizId) {
      state.currentQuiz = null;
    }
  },

  // Backward compatibility
  setLoadingTable(state, payload) {
    state.loadingQuizzes = Boolean(payload);
  },

  setLoadingPromise(state, payload) {
    state.loadingPromise = payload;
  }
};

const getters = {
  /**
   * Get all quizzes
   */
  quizzes(state) {
    return state.quizzes;
  },

  /**
   * Get current quiz
   */
  currentQuiz(state) {
    return state.currentQuiz;
  },

  /**
   * Get quiz by ID
   */
  getQuizById: state => id => {
    return state.quizzes.find(quiz => quiz.id === id) || null;
  },

  /**
   * Get quizzes by status
   */
  getQuizzesByStatus: state => status => {
    return state.quizzes.filter(quiz => quiz.status === status);
  },

  /**
   * Get published quizzes
   */
  publishedQuizzes(state) {
    return state.quizzes.filter(quiz => quiz.status === "Published");
  },

  /**
   * Get draft quizzes
   */
  draftQuizzes(state) {
    return state.quizzes.filter(quiz => quiz.status === "Draft");
  },

  /**
   * Get quizzes by category
   */
  getQuizzesByCategory: state => categoryId => {
    return state.quizzes.filter(quiz => quiz.categoryId === categoryId);
  },

  /**
   * Get loading states
   */
  loadingQuizzes(state) {
    return state.loadingQuizzes;
  },

  loadingCurrentQuiz(state) {
    return state.loadingCurrentQuiz;
  },

  savingQuiz(state) {
    return state.savingQuiz;
  },

  /**
   * Get error state
   */
  quizzesError(state) {
    return state.error;
  },

  /**
   * Check if any quiz operations are loading
   */
  quizzesIsLoading(state) {
    return state.loadingQuizzes || state.loadingCurrentQuiz || state.savingQuiz;
  },

  /**
   * Get quizzes count
   */
  quizzesCount(state) {
    return state.quizzes.length;
  },

  /**
   * Get published quizzes count
   */
  publishedQuizzesCount(state) {
    return state.quizzes.filter(quiz => quiz.status === "Published").length;
  },

  /**
   * Get draft quizzes count
   */
  draftQuizzesCount(state) {
    return state.quizzes.filter(quiz => quiz.status === "Draft").length;
  },

  /**
   * Check if quizzes exist
   */
  hasQuizzes(state) {
    return state.quizzes.length > 0;
  },

  /**
   * Check if published quizzes exist
   */
  hasPublishedQuizzes(state) {
    return state.quizzes.some(quiz => quiz.status === "Published");
  },

  /**
   * Get quizzes as options for dropdowns
   */
  quizzesOptions(state) {
    return state.quizzes.map(quiz => ({
      text: quiz.title,
      value: quiz.id
    }));
  },

  /**
   * Get published quizzes as options
   */
  publishedQuizzesOptions(state) {
    return state.quizzes
      .filter(quiz => quiz.status === "Published")
      .map(quiz => ({
        text: quiz.title,
        value: quiz.id
      }));
  },

  /**
   * Get recent quizzes (last 10)
   */
  recentQuizzes(state) {
    return [...state.quizzes]
      .sort((a, b) => new Date(b.created) - new Date(a.created))
      .slice(0, 10);
  },

  /**
   * Get total points offered by all quizzes
   */
  totalQuizzesPoints(state) {
    return state.quizzes.reduce((total, quiz) => {
      return total + (quiz.pointsOffered || 0);
    }, 0);
  },

  /**
   * Search quizzes by title
   */
  searchQuizzes: state => searchTerm => {
    if (!searchTerm) return state.quizzes;

    const term = searchTerm.toLowerCase();
    return state.quizzes.filter(
      quiz =>
        quiz.title.toLowerCase().includes(term) ||
        (quiz.description && quiz.description.toLowerCase().includes(term))
    );
  },

  /**
   * Get quiz statistics
   */
  quizzesStatistics(state) {
    const total = state.quizzes.length;
    const published = state.quizzes.filter(quiz => quiz.status === "Published")
      .length;
    const draft = state.quizzes.filter(quiz => quiz.status === "Draft").length;
    const totalPoints = state.quizzes.reduce(
      (sum, quiz) => sum + (quiz.pointsOffered || 0),
      0
    );

    return {
      total,
      published,
      draft,
      totalPoints,
      averagePoints: total > 0 ? Math.round(totalPoints / total) : 0
    };
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
